/**
 * lensePreference (HOF-106) — /farm's last selected lens (`?lense=`), remembered
 * per tenant.
 *
 * The primary selection on this page is the LensePanel lens: it decides which
 * entity type the feed renders, and it is the analogue of Preisspiegel's `?kat=`.
 * `?sub=`, `?item=`, `?year=`, `?asof=` and `?ff=` are deliberately out of scope
 * — the same scoping discipline HOF-101 applied to `?g=`/`?py=`/`?view=`. A
 * remembered sub-lens or year would be a filter the reader did not ask for on a
 * page they arrived at fresh.
 *
 * The storage half is {@link ../../utils/lensPreference}. What is /farm's own is
 * the validity rule below, and it is the one the page already applies to a URL
 * slug: the lens registry is served per tenant, so a lens that has left it must
 * fall through to the first-lens default (#2108) rather than resurrect a dead
 * slug.
 */
import { createLensPreference } from '../../utils/lensPreference';

/** Key prefix; the tenant id is appended. */
export const FARM_LENSE_PREFERENCE_PREFIX = 'hofsync.farm.lense';

const preference = createLensPreference(FARM_LENSE_PREFERENCE_PREFIX);

export const farmLensePreferenceKey = preference.key;
export const readStoredLense = preference.read;
export const writeStoredLense = preference.write;

/**
 * The remembered lens, but only if the registry still serves it.
 *
 * `undefined` lenses means "not fetched yet" and answers null: concluding "no
 * preference" there would let the caller stamp the first-lens default over a
 * preference that has not been checkable yet.
 */
export function resolveStoredLense(
  stored: string | null,
  lenses: readonly { id: string }[] | undefined,
): string | null {
  if (!stored || !lenses) return null;
  return lenses.some((l) => l.id === stored) ? stored : null;
}
